import { api } from '../../services/api';

export type Industry = 'healthcare' | 'restaurant' | 'corporate' | 'retail' | 'hospital' | 'entertainment' | 'fashion' | 'event' | 'education' | 'gym' | 'technology' | 'travel';
export type Orientation = 'landscape' | 'portrait';
export type DesignStyle = 'professional' | 'minimal' | 'bold' | 'vintage' | 'playful' | 'formal';
export type PrimaryColor = 'vibrant' | 'muted' | 'pastel' | 'monochrome' | 'neutral' | 'earthy';
export type AnimationType = 'static' | 'animated';

export interface GetTemplatesRequest {
    search?: string;
    category?: string;
    tags?: string; // Comma-separated
    industry?: Industry;
    orientation?: Orientation;
    designStyle?: DesignStyle;
    primaryColor?: PrimaryColor;
    animationType?: AnimationType;
    page?: number;
    limit?: number;
}

export interface SavedDesign {
    _id: string;
    name: string;
    thumbnail: string;
    category?: string;
    data: any;
    isSavedToCms?: boolean;
    cmsContentId?: string;
    updatedAt: string;
}

export interface Template {
    _id: string;
    name: string;
    category: string;
    orientation: Orientation;
    thumbnail: string;
    data: {
        version: string;
        width: number;
        height: number;
        layers: any[]; // Using any[] for now as the internal layer structure is complex
        elements?: any[];
        layerPositions?: Record<string, any>;
        textContents?: Record<string, string>;
        textStyles?: Record<string, any>;
        imageSettings?: Record<string, any>;
        shapeSettings?: Record<string, any>;
        backgroundSettings?: Record<string, any>;
        scrollerSettings?: Record<string, any>;
    };
    industry: Industry;
    designStyle: DesignStyle;
    primaryColor: PrimaryColor;
    animationType?: AnimationType;
}

export interface GetTemplatesResponse {
    success: boolean;
    data: Template[];
    meta: {
        total: number;
        page: number;
        limit: number;
    };
}

export interface GetSavedTemplatesResponse {
    success: boolean;
    data: SavedDesign[];
    meta: {
        total: number;
        page: number;
        limit: number;
    };
}

export const templatesApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getTemplates: builder.query<GetTemplatesResponse, GetTemplatesRequest | void>({
            query: (params) => ({
                url: '/editor/templates',
                method: 'GET',
                params: params || {},
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.data.map(({ _id }) => ({ type: 'Templates' as const, id: _id })),
                        { type: 'Templates', id: 'LIST' },
                    ]
                    : [{ type: 'Templates', id: 'LIST' }],
        }),
        getSavedTemplates: builder.query<GetSavedTemplatesResponse, { page?: number; limit?: number; search?: string } | void>({
            query: (params) => ({
                url: '/editor/saved-templates',
                method: 'GET',
                params: params || {},
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.data.map(({ _id }) => ({ type: 'SavedTemplates' as const, id: _id })),
                        { type: 'SavedTemplates', id: 'LIST' },
                    ]
                    : [{ type: 'SavedTemplates', id: 'LIST' }],
        }),
        getSavedTemplateById: builder.query<SavedDesign, string>({
            query: (id) => ({
                url: `/editor/saved-templates/${id}`,
                method: 'GET',
            }),
            providesTags: (_result, _error, id) => [{ type: 'SavedTemplates', id }],
        }),
        getSavedTemplateByCmsId: builder.query<SavedDesign, string>({
            query: (cmsContentId) => ({
                url: `/editor/saved-templates/cms/${cmsContentId}`,
                method: 'GET',
            }),
            providesTags: (_result, _error, cmsContentId) => [{ type: 'SavedTemplates', id: `cms-${cmsContentId}` }],
        }),
        getTemplateById: builder.query<Template, string>({
            query: (id) => ({
                url: `/editor/templates/${id}`,
                method: 'GET',
            }),
            providesTags: (_result, _error, id) => [{ type: 'Templates', id }],
        }),
        saveUserDesign: builder.mutation<any, FormData>({
            query: (formData) => ({
                url: '/editor/saved-templates',
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: [{ type: 'SavedTemplates', id: 'LIST' }],
        }),
        deleteSavedTemplate: builder.mutation<{ success: boolean; message: string }, string>({
            query: (id) => ({
                url: `/editor/saved-templates/${id}`,
                method: 'DELETE',
            }),
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    templatesApi.util.updateQueryData('getSavedTemplates', undefined, (draft) => {
                        if (draft && draft.data) {
                            draft.data = draft.data.filter((t) => t._id !== id);
                            if (draft.meta) {
                                draft.meta.total = Math.max(0, draft.meta.total - 1);
                            }
                        }
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: (_result, _error, id) => [
                { type: 'SavedTemplates', id },
            ],
        }),
        updateSavedTemplate: builder.mutation<SavedDesign, { id: string; data: Partial<SavedDesign> | FormData }>({
            query: ({ id, data }) => ({
                url: `/editor/saved-templates/${id}`,
                method: 'PUT',
                body: data,
            }),
            async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
                // If data is FormData, we can't easily update the cache optimistically
                if (data instanceof FormData) return;

                const patchResult = dispatch(
                    templatesApi.util.updateQueryData('getSavedTemplates', undefined, (draft) => {
                        const template = draft.data.find(t => t._id === id);
                        if (template) {
                            Object.assign(template, data);
                        }
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'SavedTemplates', id },
                { type: 'SavedTemplates', id: 'LIST' },
            ],
        }),
    }),
});

export const {
    useGetTemplatesQuery,
    useSaveUserDesignMutation,
    useGetSavedTemplatesQuery,
    useGetSavedTemplateByIdQuery,
    useGetSavedTemplateByCmsIdQuery,
    useGetTemplateByIdQuery,
    useDeleteSavedTemplateMutation,
    useUpdateSavedTemplateMutation,
} = templatesApi;
